import { forwardRef, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { useThemeMode } from "../../contexts/theme-mode";
import { createAppTheme } from "../../theme/mui-theme";
import { colors } from "../../theme/colors";
import { Box, Button, IconButton, Popover } from "@mui/material";
import NavigateBeforeRounded from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRounded from "@mui/icons-material/NavigateNextRounded";
import ReorderIcon from "@mui/icons-material/Reorder";
import PrintIcon from "@mui/icons-material/Print";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

function startOfWeekDate(date, weekStartOn) {
  const d = dayjs(date);
  const day = d.day();
  const diff = (day - weekStartOn + 7) % 7;
  return d.subtract(diff, "day").startOf("day").toDate();
}

function endOfWeekDate(date, weekStartOn) {
  return dayjs(startOfWeekDate(date, weekStartOn))
    .add(6, "day")
    .endOf("day")
    .toDate();
}

// Longest English day label for "dddd, MMMM D, YYYY" (Wednesday + September + 2-digit day).
const LONGEST_DAY_LABEL = "Wednesday, September 30, 2026";

const SchedulerCustomHeader = forwardRef(function SchedulerCustomHeader(
  {
    view,
    selectedDate,
    onDateChange,
    onViewChange,
    calendarRef,
    jumpTo,
    weekStartOn = 0,
    timeZone,
    onRearrangeClick,
    onPrintClick,
  },
  ref
) {
  const { isDark } = useThemeMode();
  const darkTheme = useMemo(() => (isDark ? createAppTheme("dark") : null), [isDark]);
  const navSx = isDark ? { color: "#f2f2f2" } : undefined;
  const [pickerAnchor, setPickerAnchor] = useState(null);

  const d = useMemo(
    () => dayjs(selectedDate).tz(timeZone),
    [selectedDate, timeZone]
  );

  const syncSchedulerDate = (next) => {
    onDateChange(next);
    try {
      calendarRef?.current?.scheduler?.handleState(next, "selectedDate");
    } catch {
      /* ignore */
    }
  };

  const applyPickedDay = (picked) => {
    if (!picked) return;
    // Use a "safe" wall-clock time in the salon timezone to avoid DST edge cases
    // while still landing on the correct calendar day.
    const wall = dayjs
      .tz(picked.format("YYYY-MM-DD") + " 12:00:00", timeZone)
      .toDate();
    syncSchedulerDate(wall);
    setPickerAnchor(null);
  };

  const dateControls = (() => {
    switch (view) {
      case "month":
        return (
          <>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Previous month"
              onClick={() => syncSchedulerDate(d.subtract(1, "month").toDate())}
            >
              <NavigateBeforeRounded />
            </IconButton>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, ...navSx }}
              aria-label="Open month picker"
              onClick={(e) => setPickerAnchor(e.currentTarget)}
            >
              {d.format("MMMM YYYY")}
            </Button>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Next month"
              onClick={() => syncSchedulerDate(d.add(1, "month").toDate())}
            >
              <NavigateNextRounded />
            </IconButton>
          </>
        );
      case "week": {
        const ws = startOfWeekDate(selectedDate, weekStartOn);
        const we = endOfWeekDate(selectedDate, weekStartOn);
        const wsD = dayjs(ws).tz(timeZone);
        const weD = dayjs(we).tz(timeZone);
        return (
          <>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Previous week"
              onClick={() =>
                syncSchedulerDate(dayjs(ws).subtract(1, "day").toDate())
              }
            >
              <NavigateBeforeRounded />
            </IconButton>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, ...navSx }}
              aria-label="Open week picker"
              onClick={(e) => setPickerAnchor(e.currentTarget)}
            >
              {`${wsD.format("MMM D")} – ${weD.format("MMM D, YYYY")}`}
            </Button>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Next week"
              onClick={() =>
                syncSchedulerDate(dayjs(we).add(1, "day").toDate())
              }
            >
              <NavigateNextRounded />
            </IconButton>
          </>
        );
      }
      case "day":
      default:
        return (
          <>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Previous day"
              onClick={() => syncSchedulerDate(d.subtract(1, "day").toDate())}
            >
              <NavigateBeforeRounded />
            </IconButton>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, position: "relative", ...navSx }}
              aria-label="Open date picker"
              onClick={(e) => setPickerAnchor(e.currentTarget)}
            >
              <Box
                component="span"
                aria-hidden
                sx={{ visibility: "hidden", display: "block", whiteSpace: "nowrap" }}
              >
                {LONGEST_DAY_LABEL}
              </Box>
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {d.format("dddd, MMMM D, YYYY")}
              </Box>
            </Button>
            <IconButton
              size="small"
              sx={navSx}
              aria-label="Next day"
              onClick={() => syncSchedulerDate(d.add(1, "day").toDate())}
            >
              <NavigateNextRounded />
            </IconButton>
          </>
        );
    }
  })();

  const pickerCalendar =
    view === "month" ? (
      <DateCalendar
        views={["year", "month"]}
        openTo="month"
        value={d}
        onChange={(next) => applyPickedDay(next)}
      />
    ) : (
      <DateCalendar
        views={["year", "month", "day"]}
        openTo="day"
        value={d}
        onChange={(next) => applyPickedDay(next)}
      />
    );

  return (
    <Box
      ref={ref}
      className="scheduler-custom-header"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        py: 0.75,
        px: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "nowrap",
          }}
        >
          {dateControls}
        </Box>
        {jumpTo ? <Box sx={{ display: "flex", alignItems: "center" }}>{jumpTo}</Box> : null}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        {typeof onPrintClick === "function" ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onPrintClick}
            startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
            aria-label="Print stylist day sheet"
            sx={{
              minWidth: 0,
              height: 32,
              borderRadius: "6px",
              boxShadow: "none",
              borderColor: isDark ? "rgba(255,255,255,0.7)" : "#000000",
              color: isDark ? "#f2f2f2" : "#000000",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": {
                borderColor: isDark ? "rgba(255,255,255,0.7)" : "#000000",
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              },
            }}
          >
            Print
          </Button>
        ) : null}
        {typeof onRearrangeClick === "function" ? (
          <Button
            size="small"
            variant="contained"
            onClick={onRearrangeClick}
            startIcon={<ReorderIcon sx={{ fontSize: 18 }} />}
            aria-label="Rearrange stylist columns"
            sx={{
              minWidth: 0,
              height: 32,
              borderRadius: "6px",
              boxShadow: "none",
              bgcolor: "#C5A06F",
              color: "#000000",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#b8915f",
                boxShadow: "none",
              },
            }}
          >
            Rearrange
          </Button>
        ) : null}
        <Button
          size="small"
          color={isDark ? "inherit" : view === "month" ? "primary" : "inherit"}
          onClick={() => onViewChange("month")}
          sx={isDark ? { color: view === "month" ? colors.gold : "#f2f2f2" } : undefined}
        >
          Month
        </Button>
        <Button
          size="small"
          color={isDark ? "inherit" : view === "week" ? "primary" : "inherit"}
          onClick={() => onViewChange("week")}
          sx={isDark ? { color: view === "week" ? colors.gold : "#f2f2f2" } : undefined}
        >
          Week
        </Button>
        <Button
          size="small"
          color={isDark ? "inherit" : view === "day" ? "primary" : "inherit"}
          onClick={() => onViewChange("day")}
          sx={isDark ? { color: view === "day" ? colors.gold : "#f2f2f2" } : undefined}
        >
          Day
        </Button>
      </Box>
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { className: isDark ? "scheduler-date-popover" : undefined },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {isDark ? (
            <ThemeProvider theme={darkTheme}>{pickerCalendar}</ThemeProvider>
          ) : (
            pickerCalendar
          )}
        </LocalizationProvider>
      </Popover>
    </Box>
  );
});

export default SchedulerCustomHeader;

